import {
  dateFormat,
  dateStringFormat,
  OrganizationDetailFragment,
  useCurrentUserQuery,
} from '@alice-financial/alice-sdk'
import { Alert, Grid, Typography } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { matchPath, Navigate, useLocation } from 'react-router'
import { FinchConnectResultAlert } from '../../externalServices/finch/FinchConnectResultAlert'
import { PageBody } from '../../routes/PageBody'
import { useCurrentOrgRootPath, useOrg } from '../organization/useOrg'
import { AliceCardProgramSection } from './AliceCardProgramSection'
import { EnrollmentSection } from './EnrollmentSection'
import { OrgHealthcheck } from './OrgHealthcheck'
import { ASSIGNMENT_ENABLED } from './config'
import { LaunchDateInput } from './LaunchDateInput'

const LaunchAlert = ({ org }: { org: OrganizationDetailFragment }) => {
  if (!org.isOnboarded || !org.launchDate) return null
  const todayIso = dateFormat.isoDate(new Date())
  const isToday = org.launchDate === todayIso
  const isFuture = org.launchDate && org.launchDate > todayIso
  if (!isToday && !isFuture) return null

  return (
    <Alert severity="info">
      <Typography variant="body2" color="text.secondary" fontWeight="bold">
        Onboarding requirements complete. Benefits will launch{' '}
        {dateFormat.medium(dateStringFormat.dateFromDateString(org.launchDate))}
      </Typography>
      <Grid container alignItems="center">
        <Grid flex={1}>
          <Typography variant="body2">
            {!org.launchDate && 'Choose when your employees should receive enrollment invitations.'}
            {isToday && 'Your employees can now start enrolling.'}
            {org.launchDate && isFuture && (
              <>
                Employees will receive enrollment invitations starting on{' '}
                {dateFormat.short(dateStringFormat.dateFromDateString(org.launchDate))}. You may choose a
                different date any time before then.
              </>
            )}
          </Typography>
        </Grid>
        <LaunchDateInput org={org} />
      </Grid>
    </Alert>
  )
}

const checkForcedLocation = (org: OrganizationDetailFragment | undefined | null, orgRootPath: string) => {
  if (org && !org.isOnboarded) {
    // handle required onboarding steps - when these are complete, other steps can be completed in any order
    if (!org.contractSignedOn) return `${orgRootPath}/order-form`
    // if (!hasBillingConnected(org)) return `${orgRootPath}/billing`
  }
  return null
}

export const OrgHomepage = () => {
  const orgRootPath = useCurrentOrgRootPath()
  const { data: userData } = useCurrentUserQuery()
  const org = useOrg()
  const user = userData?.currentUser
  const { pathname } = useLocation()

  const forcedLocation = checkForcedLocation(org, orgRootPath)
  if (forcedLocation && !matchPath(forcedLocation, pathname)) return <Navigate to={forcedLocation} replace />

  if (!user || !org) return null
  return (
    <PageBody maxWidth={ASSIGNMENT_ENABLED && !org.isOnboarded ? 'lg' : 'md'}>
      <LaunchAlert org={org} />
      <OrgHealthcheck org={org} />
      <FinchConnectResultAlert />
      <AliceCardProgramSection org={org} />
      <EnrollmentSection org={org} />
    </PageBody>
  )
}
